/* eslint-disable max-len */
import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';

import styled, {css} from 'styled-components';

import api, {graphqlQueryBuild} from '../services/api';
import {slugify} from './../helpers';
import {
    Flex,
    notWebMediaQuery,
    PageBodyWrapper,
    mobileMediaQuery,
    tabletMediaQuery,
    useBreakpoint,
    breakpoints,
} from '../styles';

import Loader from '../components/Loader';
import GamesGrid from '../components/GamesGrid';
import Button from '../components/Button';
import AdsPlayer from '../components/AdsPlayer';

import {
    showAnimation,
    closeAnimation,
} from '../components/UnlockModal/animations';

import {ReactComponent as CloseIcon} from '../assets/close.svg';
import FeaturedGameSlider from '../components/FeaturedGamesSlider';
import GameRecentView from '../components/GameRecentView';
import {saveGamePlayed} from '../store/actions/user';

const Games = props => {
    const {params} = useRouteMatch();
    const {t} = useTranslation();
    const history = useHistory();
    const {user} = useSelector(state => state);
    const idp = user && user.idp;

    const bodyElement = document.querySelector('body');

    const isWeb = !['xs', 'mobile', 'sm', 'md'].includes(useBreakpoint());

    const [games, setGames] = useState(undefined);
    const [currentGame, setCurrentGame] = useState(undefined);
    const [showExitModal, setShowExitModal] = useState(false);
    const [hideExitModal, setHideExitModal] = useState(true);
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [adsFinished, setAdsFinished] = useState(false);
    const [closeGame, setCloseGame] = useState(false);
    const [adsStarted, setAdsStarted] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [documentHidden, setDocumentHidden] = useState(document.hidden);

    const dispatch = useDispatch();
    useEffect(() => {
        if (currentGame) {
            dispatch(saveGamePlayed(currentGame.id));
        }
    }, [currentGame, dispatch]);

    useEffect(() => {
        document.addEventListener(
            'visibilitychange',
            checkDocumentVisibility,
            false,
        );

        window.addEventListener(
            'focus',
            () => {
                !isFocused && setIsFocused(true);
            },
            false,
        );
        window.addEventListener(
            'click',
            () => {
                !isFocused && setIsFocused(true);
            },
            false,
        );

        window.turner_metadata.trackAction.push({
            type: 'pageview',
            data: {
                pageName: 'cnplus.com/games',
                section: 'Games',
                subsection: 'nvs',
                contenttype: 'Games',
                contenttitle: 'Games',
                presentationtemplate: 'desktop',
                platform: 'web',
                domain: 'cnplus.com',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                brand: 'cartoon network',
                pageviewevent: 1,
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
            },
        });

        return () => {
            document.removeEventListener(
                'visibilitychange',
                checkDocumentVisibility,
            );
        };

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        handleWindowResize();
        window.addEventListener('resize', handleWindowResize);
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (currentGame && adsFinished && !documentHidden) {
            setTimeout(() => {
                const iframe = document.querySelector('iframe');

                try {
                    if (
                        iframe &&
                        iframe.contentWindow &&
                        iframe.contentWindow.document
                    ) {
                        iframe.contentWindow.document.body.style.overflow =
                            'hidden';
                    }
                } catch (err) {}
            }, 250);
        }

        // eslint-disable-next-line
    }, [currentGame, adsFinished, documentHidden]);

    const handleWindowResize = () => {
        setScreenSize({
            width: window.innerWidth,
            height: window.innerHeight,
        });
    };

    useEffect(() => {
        handleLoadGames();

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (currentGame && !closeGame) {
            let top = window.scrollY;

            bodyElement.style.position = 'fixed';
            bodyElement.style.top = `-${top}px`;
            bodyElement.style.left = '0px';
            bodyElement.style.right = '0px';
        } else {
            const scrollY = bodyElement.style.top;

            bodyElement.style.position = '';
            bodyElement.style.top = '';
            bodyElement.style.left = '';
            bodyElement.style.right = '';

            window.scrollTo(0, -parseInt(scrollY || '0'));
        }

        return () => {
            const scrollY = bodyElement.style.top;

            bodyElement.style.position = '';
            bodyElement.style.top = '';
            bodyElement.style.left = '';
            bodyElement.style.right = '';

            window.scrollTo(0, -parseInt(scrollY || '0'));
        };

        // eslint-disable-next-line
    }, [currentGame, closeGame]);

    useEffect(() => {
        setTimeout(
            () => {
                setHideExitModal(!showExitModal);
            },
            showExitModal ? 0 : 500,
        );
    }, [showExitModal]);

    const checkDocumentVisibility = () => {
        setDocumentHidden(document.hidden);
    };

    const handleLoadGames = async () => {
        let query = graphqlQueryBuild(
            'games',
            `
                id
                title
                description
                url
                orientation
                image
                showName
                internationalCategory
                internationalTitle
                soundIssue
            `,
            '',
            false,
            false,
        );

        let response = await api
            .get('', {
                params: {
                    query,
                },
            })
            .then(res => res)
            .catch(err => err.response);

        if (response.data.data && response.data.data.games) {
            const tempGames = response.data.data.games.reduce((acc, game) => {
                game = {...game, metadata: {contentId: game.id}};
                return [...acc, game];
            }, []);

            setGames(tempGames);

            let game_id = params.game_id || '';
            game_id = parseInt(
                game_id.substring(game_id.lastIndexOf('-') + 1),
            ).toString();

            if (
                params.game_id &&
                tempGames.find(game => game.id.toString() === game_id)
            ) {
                handleClickGame(
                    game_id,
                    tempGames.find(game => game.id.toString() === game_id),
                );
            } else {
                history.replace('/games');
            }
        } else {
            history.push('/error');
        }
    };

    const handleClickGameFeatured = (gameId, item) =>
        handleClickGame(gameId, item, true);

    const handleClickGame = (gameId, item, featured = false) => {
        const gameUrl = `/games/play/${slugify(item.title)}-${gameId}`;

        history.location.pathname !== gameUrl &&
            history.push({
                pathname: gameUrl,
                state: {item},
            });

        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                pageName: 'cnplus.com/games',
                section: 'Games',
                contenttype: 'Games',
                contenttitle: 'Games',
                platform: 'web',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                brand: 'cartoon network',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                franchise: item.showName,
                englishname: item.internationalTitle,
                gamecategory: item.internationalCategory,
                gametitle: item.title,
                gameclickedevent: 1,
                ...((featured && {
                    featuredcontent: featured ? item.title : undefined,
                    featuredccontentevent: featured ? 1 : undefined,
                }) ||
                    {}),
            },
        });

        setCurrentGame(item);
        setAdsFinished(false);
        setAdsStarted(false);
        setCloseGame(false);
        setShowExitModal(false);

        //add page view tracking
        window.turner_metadata.trackAction.push({
            type: 'pageview',
            data: {
                pageName: `cnplus.com/games/${item.internationalTitle}`,
                section: 'Games',
                subsection: 'nvs',
                contenttype: 'Games',
                contenttitle: 'Games',
                presentationtemplate: 'desktop',
                platform: 'web',
                domain: 'cnplus.com',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                brand: 'cartoon network',
                pageviewevent: 1,
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                englishname: item.internationalTitle,
                gametitle: item.title,
            },
        });
    };

    const handleCloseGame = () => {
        if (!adsFinished) {
            setCloseGame(true);
            setTimeout(() => handleConfirmClose(true), adsStarted ? 0 : 250);
            history.push(`/games`);
        } else {
            setShowExitModal(!showExitModal);
        }
    };

    const handleConfirmClose = (confirm = false) => {
        setShowExitModal(false);
        if (confirm) {
            window.turner_metadata.trackAction.push({
                type: 'interaction',
                data: {
                    pageName: 'cnplus.com/games',
                    section: 'Games',
                    contenttype: 'Games',
                    contenttitle: 'Games',
                    platform: 'web',
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    brand: 'cartoon network',
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    franchise: currentGame.showName,
                    englishname: currentGame.internationalTitle,
                    gamecategory: currentGame.internationalCategory,
                    gametitle: currentGame.title,
                    gameexitevent: 1,
                },
            });

            setCurrentGame(undefined);
            setAdsFinished(false);
            setAdsStarted(false);
            setCloseGame(false);
            history.push(`/games`);
        }
    };

    return (
        <>
            <Helmet>
                <title>{t('games.metaTitle')}</title>
                <meta name="description" content={t('games.metaDescription')} />
            </Helmet>

            <Container>
                {!games ? (
                    <Loader background="#009bdf" />
                ) : (
                    <>
                        <FeaturedGameSlider
                            infinite={true}
                            startIndex={0}
                            horizontalScroll={true}
                            items={games.slice(0, 3)}
                            className="slider-frame"
                            handleClickContent={handleClickGameFeatured}
                            onClickContent
                            isGames
                            featured
                            hideControls={!isWeb}
                            snapSlides={'center'}
                            indicators={true}
                        />

                        <GameRecentView
                            allGames={games}
                            width={Math.min(screenSize.width, 1440)}
                            handleClickGame={handleClickGame}
                        />

                        <Spacer />

                        <SectionTitle>{t('games.allGames')}</SectionTitle>

                        <GamesGrid
                            items={[...games.slice(3), ...games.slice(0, 3)]}
                            hidePlayButton
                            onClickItem={handleClickGame}
                        />
                    </>
                )}
            </Container>

            {currentGame && (
                <GamePlayerContainer
                    screenSize={screenSize}
                    orientation={currentGame.orientation}
                    background={currentGame.image}
                    hideIframe={showExitModal}
                    closed={closeGame}
                >
                    {!hideExitModal && (
                        <ExitModal direction="column" show={showExitModal}>
                            <h2>{t('games.exitModal.title')}</h2>

                            <p>{t('games.exitModal.subtitle')}</p>

                            <Button
                                width="224px"
                                margin="0 0 16px 0"
                                background="linear-gradient(90deg, #F55A86 0%, #EC098C 100%);"
                                value={t('games.exitModal.keepPlaying')}
                                onClick={() => handleConfirmClose(false)}
                            />

                            <Button
                                width="224px"
                                background="#d7d7d7"
                                color="#000000"
                                value={t('games.exitModal.exitGame')}
                                onClick={() => handleConfirmClose(true)}
                            />
                        </ExitModal>
                    )}

                    {!adsFinished && (
                        <AdsPlayer
                            closed={closeGame}
                            hasFocus={isFocused}
                            eventHandlers={{
                                onCompleted: () => {
                                    setAdsFinished(true);
                                },
                                onStarted: () => {
                                    setAdsStarted(true);
                                },
                            }}
                            append={
                                <AdsMessage>
                                    <img
                                        alt="Game Thumbnail"
                                        src={currentGame.image}
                                    />
                                    <span>{t('games.ad.disclaimer')}</span>
                                </AdsMessage>
                            }
                        />
                    )}

                    {adsFinished &&
                        (currentGame.soundIssue ? !documentHidden : true) && (
                            <iframe
                                title={currentGame.title}
                                src={currentGame.url}
                            />
                        )}

                    {!showExitModal && (
                        <CloseButton onClick={handleCloseGame}>
                            <CloseIcon title={t('games.exitModal.exitGame')} />
                        </CloseButton>
                    )}
                </GamePlayerContainer>
            )}
        </>
    );
};

const Container = styled(PageBodyWrapper)`
    width: 100%;
    align-self: stretch;
    flex: 1;
    padding: 56px 0;
    min-height: calc(100vh - 392px);

    ${notWebMediaQuery} {
        padding-top: 20px;
    }
`;

const CloseButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0px;
    right: 0px;
    margin: 44px;

    height: 20px;
    width: 20px;
    box-sizing: content-box;

    ${tabletMediaQuery} {
        margin: 28px 20px 0 0;
    }

    ${mobileMediaQuery} {
        margin: 18px 15px 0 0;
    }

    border: none;
    background-color: transparent;
    padding: 0;
    cursor: pointer;

    svg {
        width: 20px;
        height: 20px;
        g {
            fill: #fff;
        }
    }
`;

const GamePlayerContainer = styled.div`
    background: linear-gradient(
            to right,
            rgba(0, 0, 0, 0.85),
            rgba(0, 0, 0, 0.85)
        ),
        url(${props => props.background}) no-repeat center center;
    background-size: cover;
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({closed}) =>
        closed &&
        css`
            display: none;
        `}

    & > iframe {
        margin: 40px 0;
        box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
        border-radius: 6px;
        background-color: rgba(8, 0, 8);
        border: none;
        overflow: hidden;
        width: ${props =>
            props.orientation === 'landscape'
                ? '100%'
                : `${Math.min(
                      props.screenSize.height * 0.5625,
                      props.screenSize.width * 0.9,
                  )}px`};
        height: calc(
            ${props =>
                props.orientation === 'landscape'
                    ? `${Math.min(
                          props.screenSize.width * 0.5625,
                          props.screenSize.height * 0.9,
                      )}px`
                    : '100%'}
        );
        max-width: ${props => Math.min(1440, props.screenSize.width * 0.9)}px;
        max-height: calc(90% - 50px);

        ${mobileMediaQuery}, screen and (max-height: ${breakpoints.mobile}px)  {
            width: 100%;
            height: 100%;
            border-radius: 0px;
            max-width: initial;
            max-height: initial;

            margin: 0px;
            padding-top: 50px;
            box-sizing: border-box;
        }

        opacity: 1;
        transition: opacity 0.3s ease-in-out;

        ${props =>
            props.hideIframe &&
            css`
                pointer-events: none;
                opacity: 0;
            `};
    }
`;

const ExitModal = styled(Flex)`
    position: absolute;
    padding: 40px;
    border-radius: 16px;
    background-color: #fff;
    box-sizing: border-box;
    width: 535px;
    height: auto;
    max-width: 90%;

    ${props =>
        !props.show
            ? css`
                  transform: translateY(100vh);
                  animation-name: ${closeAnimation};
                  animation-duration: 0.5s;
                  animation-timing-function: cubic-bezier(0.22, 0.61, 0.35, 1);
                  animation-fill-mode: forwards;
              `
            : css`
                  animation-name: ${showAnimation};
                  animation-duration: 0.5s;
                  animation-timing-function: cubic-bezier(0.22, 0.61, 0.35, 1);
                  animation-fill-mode: forwards;
              `};

    h2,
    p {
        color: #000;
    }

    h2 {
        font-family: 'TTNormsProExtraBlack';
        font-size: 24px;
        line-height: 1.125;
        font-weight: bold;
        text-align: center;
        text-transform: uppercase;
        margin: 0 0 16px 0;
    }

    p {
        /* font-family: 'AvenirNextRoundedStd'; */
        font-size: 18px;
        line-height: 24px;
        text-align: center;
        max-width: 316px;
        margin: 0 0 32px 0;
    }

    @media screen and (max-width: 380px) {
        padding: 40px 20px;
    }
`;

const SectionTitle = styled.h2`
    color: white;
    font-family: 'TTNormsProExtraBlack';
    font-size: 20px;
    line-height: 1;
    text-transform: uppercase;
    margin: 0 auto 16px 40px;

    ${mobileMediaQuery} {
        margin: 0 auto 16px 25px;
    }
`;

const Spacer = styled.div`
    height: 56px;

    ${tabletMediaQuery} {
        height: 32px;
    }

    ${mobileMediaQuery} {
        height: 24px;
    }
`;

const AdsMessage = styled(Flex)`
    margin-top: 56px;
    background: rgb(0, 0, 0, 0.5);
    border-radius: 8px;

    overflow: hidden;
    max-width: 80%;
    max-height: 74px;
    flex-wrap: nowrap;

    img {
        height: 72px;
        width: 131px;
        margin-right: 39px;

        align-self: stretch;
        height: auto;
        object-fit: cover;
    }

    span {
        color: rgb(255, 255, 255);
        font-size: 18px;
        font-weight: 600;
        line-height: 31px;
        padding: 20px 47px 20px 0;

        ${mobileMediaQuery} {
            font-size: 16px;
        }
    }

    ${tabletMediaQuery} {
        margin-top: 40px;
    }

    ${mobileMediaQuery} {
        margin-top: 38px;
    }

    @media screen and (max-width: 415px) {
        img {
            margin-right: 24px;
        }
        span {
            font-size: 14px;
            line-height: 1.15;
            padding-right: 24px;
        }
    }
`;

export default Games;
